import {useCampaignInfluencerRatingMutation, useCampaignReportingApplicationsQuery} from "@/api/campaignApi";
import LockIcon from "@/assets/colorable/lock.svg?react";
import DefaultCarouselFooter from "@/components/Campaign/management/components/carousel/footer/DefaultCarouselFooter";
import InfluencerRatingSection from "@/components/Campaign/management/reportingTab/InfluencerRatingSection";
import Button from "@/components/Common/Button";
import AutosizeTextarea from "@/components/Common/forms/AutosizeTextarea";
import {Tooltip} from "@/components/Common/Tooltip";
import clsxm from "@/lib/clsxm";
import type {TCarouselMedia} from "@/store/carousel";
import {RATING_COMMENT_MAX_CHARS} from "@/store/constants";
import {useId, useState, type FC} from "react";
import {useTranslation} from "react-i18next";
import {useStateWithDeps} from "use-state-with-deps";

const COMMENT_COUNTER_THRESHOLD_CHARS = 450;

const RatingCarouselFooter: FC<{content: TCarouselMedia}> = ({content}) => {
  const {t} = useTranslation(["common", "campaign_reporting"]);
  const [ratedApplicationIds, setRatedApplicationIds] = useState<Set<number>>(new Set());

  const carouselApplication = content.instagram_media?.campaign_application ?? undefined;

  const {data: reportingApplications, isLoading: areReportingApplicationsLoading} =
    useCampaignReportingApplicationsQuery({
      queryOptions: {enabled: !carouselApplication && !!content.applicationId},
    });
  const reportingApplication = reportingApplications?.find(application => application.id === content.applicationId);

  const application = reportingApplication ?? carouselApplication;
  const firstName = reportingApplication?.influencer.user.first_name ?? content.user?.first_name ?? "";

  const isRated = !!application && (!!application.rated_influencer || ratedApplicationIds.has(application.id));
  const canBeRated = !!reportingApplication || carouselApplication?.can_be_rated !== false;

  const [ratingState, setRatingState] = useStateWithDeps({comment: "", contentQuality: 0, reliability: 0}, [
    application?.id,
  ]);
  const isCommentCounterVisible = ratingState.comment.length >= COMMENT_COUNTER_THRESHOLD_CHARS;
  const commentVisibilityTooltipId = useId();
  const {mutate: rateInfluencer, isPending: isRatingPending} = useCampaignInfluencerRatingMutation({
    applicationId: application?.id,
    campaignId: application?.campaign?.id,
    onSuccess: () => application && setRatedApplicationIds(previousIds => new Set([...previousIds, application.id])),
    userId: reportingApplication?.influencer.user.id ?? content.user?.id ?? undefined,
  });

  if (areReportingApplicationsLoading) {
    return null;
  }

  if (!application || isRated || !canBeRated) {
    return <DefaultCarouselFooter content={content} />;
  }

  return (
    <div className="flex w-full flex-col items-center justify-center gap-2.5" onClick={e => e.stopPropagation()}>
      <div className="border-neutral-black bg-neutral-black-jet divide-neutral-black flex w-full flex-col divide-y rounded-md border">
        <InfluencerRatingSection
          className="divide-neutral-black w-full flex-row gap-0 divide-x max-sm:gap-0"
          contentQuality={ratingState.contentQuality}
          contentQualityTitle={t("campaign_reporting:compact_quality_title", {influencerName: firstName})}
          firstName={firstName}
          ratingClassName="flex-1 px-5 py-3"
          starClassName="max-sm:size-6 size-6"
          textClassName="text-white max-sm:text-2xs text-2xs leading-3 sm:text-nowrap"
          onRateQuality={(value: number) => setRatingState(previous => ({...previous, contentQuality: value}))}
          onRateReliability={(value: number) => setRatingState(previous => ({...previous, reliability: value}))}
          reliability={ratingState.reliability}
          reliabilityTitle={t("campaign_reporting:compact_reliability_title", {influencerName: firstName})}
        />
        <div className="flex w-full items-center gap-5 py-2.5 pr-5 pl-4">
          <div className="relative min-w-0 flex-1">
            <AutosizeTextarea
              className={clsxm(
                "border-neutral-black bg-neutral-black placeholder:text-neutral-grey-dark focus:border-neutral-grey-dark scrollbar-thin-dark min-h-10 p-3 text-xs leading-4 text-white placeholder:text-xs md:placeholder:text-xs",
                isCommentCounterVisible ? "pr-16" : "pr-8",
              )}
              maxLength={RATING_COMMENT_MAX_CHARS}
              maxRows={2}
              minRows={1}
              onChange={event => setRatingState(previous => ({...previous, comment: event.target.value}))}
              onKeyDown={event => event.stopPropagation()}
              placeholder={t("campaign_reporting:rating_comment_inline_placeholder")}
              value={ratingState.comment}
            />
            <LockIcon
              className="text-neutral-grey-dark absolute top-3.5 right-3 size-3"
              data-tooltip-id={commentVisibilityTooltipId}
            />
            <Tooltip className="max-w-60 text-xs" id={commentVisibilityTooltipId} place="top" variant="light">
              {t("campaign_reporting:rating_comment_visibility", {influencerName: firstName})}
            </Tooltip>
            {isCommentCounterVisible && (
              <span
                className={clsxm(
                  "text-2xs pointer-events-none absolute right-3 bottom-1.5",
                  ratingState.comment.length >= RATING_COMMENT_MAX_CHARS
                    ? "text-danger-medium-red"
                    : "text-neutral-grey-dark",
                )}>
                {ratingState.comment.length} / {RATING_COMMENT_MAX_CHARS}
              </span>
            )}
          </div>
          <Button
            buttonStyle="Gradient"
            className="shrink-0 px-8 max-sm:h-10 max-sm:px-5 max-sm:text-sm"
            isDisabled={ratingState.contentQuality === 0 || ratingState.reliability === 0}
            isLoading={isRatingPending}
            onClick={() => rateInfluencer(ratingState)}>
            {t("rate")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RatingCarouselFooter;
